import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { planKeys } from '@/services/queryKeys';
import {
  planService,
  type PlanInfo,
} from '@/services/plan.service';
import { setPlan } from '@/redux/slices/planSlice';
import type { AppDispatch } from '@/redux';

function isValidPlanInfo(v: unknown): v is PlanInfo {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.tier === 'string' && typeof o.usage === 'object' && o.usage !== null;
}

  // EG-80 — Transform JSON snake_case (BE contract per ADR-013) sang camelCase
// Redux internal state. ADR-013 cấm mapping ở response/DTO layer; mapping
// service-DTO → state ở đây là FE-internal modeling, KHÔNG phải transform
// API contract.
function toPlanState(p: PlanInfo) {
  return {
    tier: p.tier,
    expiresAt: p.expires_at,
    trialDaysLeft: p.trial_days_left,
    graceDaysLeft: p.grace_days_left,
    features: p.features ?? [],
    usage: {
      products: p.usage.products,
      staffs: p.usage.staffs,
      stores: p.usage.stores,
      monthlyOrders: p.usage.monthly_orders,
    },
  };
}

/**
 * Load plan info + sync to Redux on mount.
 */
export function usePlanData() {
  const dispatch = useDispatch<AppDispatch>();
  const query = useQuery({
    queryKey: planKeys.current(),
    queryFn: () => planService.current(),
    retry: 0,
  });

  useEffect(() => {
    if (query.data && isValidPlanInfo(query.data)) {
      dispatch(setPlan(toPlanState(query.data)));
    }
  }, [query.data, dispatch]);

  return query;
}

export function usePaymentHistory() {
  return useQuery({
    queryKey: planKeys.payments(),
    queryFn: () => planService.paymentHistory(),
    retry: 0,
  });
}

export function useBillingPayments(page = 1, limit = 20) {
  return useQuery({
    queryKey: planKeys.billingPayments(page, limit),
    queryFn: () => planService.getBillingPayments(page, limit),
    retry: 1,
  });
}
